import * as React from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { User, NotificationPreferences } from '@/types';
import { UserRole } from '@/types/user';
import { supabase } from '@/integrations/supabase/client';
import { isStandaloneAuthPortalRoute, clearUserFromStorage } from './authUtils';
import AuthErrorFallback from './AuthErrorFallback';

interface AuthContextType {
  user: User | null;
  supabaseUser: SupabaseUser | null;
  session: Session | null;
  loading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUserRole: (role: User['role']) => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

const defaultPreferences: NotificationPreferences = {
  email: true,
  sms: false,
  push: true,
  emergencyOnly: false,
  maintenanceUpdates: true,
  vendorCommunications: false,
};

// Auth state machine states to prevent race conditions
type AuthState = 'idle' | 'initializing' | 'authenticated' | 'unauthenticated' | 'error';

// Helper to convert Supabase user to app User
const createUserFromSession = async (supabaseUser: SupabaseUser): Promise<User> => {
  // Try to fetch profile from database
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', supabaseUser.id)
    .single();

  if (error) {
    console.log('No profile found, using metadata:', error.message);
  }

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: profile?.name || supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
    role: (profile?.role as UserRole) || (supabaseUser.user_metadata?.role as UserRole) || 'tenant',
    phone: profile?.phone || supabaseUser.phone || undefined,
    assignedProperties: [],
    preferences: defaultPreferences,
    isActive: profile?.is_active ?? true,
    createdAt: profile?.created_at || supabaseUser.created_at,
    lastLogin: new Date().toISOString(),
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = React.useState<SupabaseUser | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  
  // State machine to prevent race conditions between getSession and onAuthStateChange
  const authStateRef = React.useRef<AuthState>('idle');
  // Track the session ID to detect stale updates
  const currentSessionIdRef = React.useRef<string | null>(null);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = React.useRef(true);

  // Safe state setter that checks if component is still mounted
  const safeSetState = React.useCallback(<T,>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  React.useEffect(() => {
    isMountedRef.current = true;
    
    // NOTE: We no longer skip auth initialization for standalone auth portal routes.
    // Those pages handle their own auth UX, but AuthProvider must still track
    // session state so that post-login navigation works correctly.

    console.log('AuthProvider: useEffect starting');
    authStateRef.current = 'initializing';

    // Process session and update state - shared logic to prevent duplication
    const processSession = async (currentSession: Session | null, source: string) => {
      if (!isMountedRef.current) return;
      
      const sessionId = currentSession?.access_token || null;
      
      // Skip if this session was already processed (prevents race condition)
      if (sessionId && sessionId === currentSessionIdRef.current) {
        console.log(`AuthProvider: [${source}] Session already processed, skipping`);
        return;
      }
      
      currentSessionIdRef.current = sessionId;
      
      if (currentSession?.user) {
        console.log(`AuthProvider: [${source}] Processing session for:`, currentSession.user.email);
        safeSetState(setSession, currentSession);
        safeSetState(setSupabaseUser, currentSession.user);
        
        try {
          const appUser = await createUserFromSession(currentSession.user);
          if (isMountedRef.current) {
            setUser(appUser);
            authStateRef.current = 'authenticated';
          }
        } catch (error) {
          console.error(`AuthProvider: [${source}] Error creating user from session:`, error);
          if (isMountedRef.current) {
            authStateRef.current = 'error';
          }
        }
        
        // Clear any legacy localStorage data
        clearUserFromStorage();
      } else {
        console.log(`AuthProvider: [${source}] No session`);
        if (isMountedRef.current) {
          authStateRef.current = 'unauthenticated';
        }
      }
    };

    // Set up auth state listener FIRST
    console.log('AuthProvider: Setting up auth state listener');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (!isMountedRef.current) return;
      
      try {
        console.log('AuthProvider: Auth state changed:', event);

        if (event === 'SIGNED_IN') {
          // Set session/user synchronously FIRST to prevent race condition
          // This ensures TenantMobileApp sees the user immediately
          if (currentSession) {
            safeSetState(setSession, currentSession);
            safeSetState(setSupabaseUser, currentSession.user);
          }
          // Then process profile asynchronously
          await processSession(currentSession, 'onAuthStateChange:SIGNED_IN');
        } else if (event === 'SIGNED_OUT') {
          console.log('AuthProvider: User signed out, clearing data');
          currentSessionIdRef.current = null;
          safeSetState(setSession, null);
          safeSetState(setSupabaseUser, null);
          safeSetState(setUser, null);
          clearUserFromStorage();
          authStateRef.current = 'unauthenticated';
        } else if (event === 'TOKEN_REFRESHED' && currentSession?.user) {
          console.log('AuthProvider: Token refreshed');
          // Update session reference but don't re-fetch profile
          currentSessionIdRef.current = currentSession.access_token;
          safeSetState(setSession, currentSession);
          safeSetState(setSupabaseUser, currentSession.user);
        }

        console.log('AuthProvider: Auth state change handled');
      } catch (error) {
        console.error('Error in auth state change handler:', error);
        if (isMountedRef.current) {
          setHasError(true);
          setLoading(false);
        }
      }
    });

    // Then get initial session
    const getInitialSession = async () => {
      try {
        console.log('AuthProvider: Getting initial session');

        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
          if (isMountedRef.current) {
            setHasError(true);
            setLoading(false);
          }
          return;
        }

        await processSession(currentSession, 'getInitialSession');
      } catch (error) {
        console.error('Error in getSession:', error);
        if (isMountedRef.current) {
          setHasError(true);
        }
      } finally {
        if (isMountedRef.current) {
          console.log('AuthProvider: Setting loading to false');
          setLoading(false);
        }
      }
    };

    getInitialSession();

    return () => {
      isMountedRef.current = false;
      try {
        console.log('AuthProvider: Cleaning up subscription');
        subscription.unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing from auth changes:', error);
      }
    };
  }, [safeSetState]);
  
  // Login function - now just for setting user after Supabase auth
  const login = React.useCallback((newUser: User) => {
    console.log('AuthProvider: Setting user:', newUser.email);
    setUser(newUser);
  }, []);

  const updateUserRole = React.useCallback((role: User['role']) => {
    if (user) {
      const updatedUser = { ...user, role };
      setUser(updatedUser);
    }
  }, [user]);

  const logout = React.useCallback(async () => {
    console.log('AuthProvider: Logging out');
    // Clear local user first
    setUser(null);
    clearUserFromStorage();
    currentSessionIdRef.current = null;
    
    // Sign out from Supabase
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error in signOut:', error);
    }
  }, []);
  
  const signOut = logout; // Alias for consistency
  
  // Render error fallback if AuthProvider encounters an error
  if (hasError) {
    return <AuthErrorFallback />;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      supabaseUser, 
      session, 
      loading, 
      login, 
      logout, 
      signOut,
      updateUserRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    // More detailed error for debugging
    console.error('useAuth hook called outside of AuthProvider context');
    throw new Error('useAuth must be used within an AuthProvider. Make sure your component is wrapped with AuthProvider.');
  }
  return context;
};

// Safe hook that returns null if context is not available
export const useAuthSafe = (): AuthContextType | null => {
  const context = React.useContext(AuthContext);
  return context || null;
};
